<template>
  <NuxtLayout>
    <div class="flex flex-col bg-gray-950">
      <SiteHeaderRibbon />

      <ToastStack>
        <Notification :show-notification="showNotification" message="Palette Link Copied!" :auto-hide-duration="2000" @update:show-notification="showNotification = false" />
        <Notification :show-notification="showNotificationColor" message="Color Copied!" :auto-hide-duration="2000" @update:show-notification="showNotificationColor = false" />
        <Notification :show-notification="showNotificationColorAdded" message="Color added to palette!" :auto-hide-duration="2000" @update:show-notification="showNotificationColorAdded = false" />
        <FilterToast
          v-if="showFilter && totalCodeSelectedColors + totalVisualSelectedColors !== 0 && enableEdit === true"
          :count="totalVisualSelectedColors + totalCodeSelectedColors"
          @keep="mergePalettes"
          @cancel="resetFiltering"
        />
      </ToastStack>

      <!-- Left fixed panel: branding + URL generator + screenshot sampler -->
      <div class="relative flex-none overflow-hidden px-6 lg:pointer-events-none lg:fixed lg:inset-0 lg:z-40 lg:flex lg:px-0">
        <div class="relative z-10 flex w-full border-0 border-gray-900 lg:pointer-events-auto lg:mr-[calc(max(2rem,50%-38rem)+40rem)] lg:min-w-[32rem] lg:overflow-y-auto lg:overflow-x-hidden lg:border-r lg:pl-[max(4rem,calc(50%-38rem))]">
          <div class="mx-auto max-w-lg lg:mx-0 lg:flex lg:w-96 lg:max-w-none lg:flex-col lg:before:flex-1 lg:before:pt-6">
            <div>
              <HeroBackdrop />
              <div class="relative my-24 lg:my-6">
                <a href="/">
                  <img width="250" height="38" :src="logo" alt="colorize.design" />
                </a>
                <!-- The headline gives way to the screenshot. Both lines collapse as
                     one block (padding instead of a top margin, so the spacing is
                     inside the animated box) and the form below slides up with it. -->
                <CollapseTransition>
                  <div v-if="screenshotLoaded === false" class="pt-10">
                    <BaseHeading as="h1" size="display">Generate <GradientText>color palettes</GradientText> from any website.</BaseHeading>
                    <h2 class="mt-6 text-lg leading-8 text-gray-300">Extract grouped color combos from any website. Enter a URL to get a design palette.</h2>
                  </div>
                </CollapseTransition>

                <UrlGeneratorForm
                  v-model:url="urlData"
                  v-model:token="token"
                  :is-loading="isLoading"
                  :pending-generate="pendingGenerate"
                  :has-error="hasError"
                  :has-warning="hasWarning"
                  :has-success="hasSuccess"
                  :error-msg="errorMsg"
                  :favicon="response.favicon"
                  :typed-url="typedUrl"
                  :setting-toggle="settingToggle"
                  :options="mailingLists"
                  :turnstile-options="turnstileOptions"
                  :set-turnstile-ref="setTurnstileRef"
                  :screenshot-src="response.screenshot"
                  :screenshot-loaded="screenshotLoaded"
                  :screenshot-animation="screenshotAnimation"
                  :is-mobile="isMobile"
                  @generate="getUrl"
                  @toggle-settings="toggleSettings"
                  @toggle-check="toggleCheck"
                  @slide="(option, value) => (option.currentVal = value)"
                  @sampled="onScreenshotSampled"
                  @screenshot-loaded="onScreenshotLoaded"
                />
              </div>
            </div>
            <!-- The crawl path to the brand-colour landing pages now runs
                 through the "Brand colors" button in FeedbackBar. -->
            <div class="flex flex-1 items-end justify-center pb-4 lg:justify-start lg:pb-6">
              <PrefetchCredit />
            </div>
          </div>
        </div>
      </div>

      <!-- Right content area: palette toolbar, tabs and color groups -->
      <div class="mb-10 min-h-screen space-y-8 sm:py-20">
        <div class="scroll-mt-16" ref="scrollElement">
          <SectionColumn :dim="dimContent" inner-class="relative">
            <PaletteToolbar
              v-model="title"
              :is-loading="isLoading"
              :enable-edit="enableEdit"
              :download-open="isDownloadModalOpen"
              @share="openNotification"
              @toggle-edit="editToggle"
              @open-download="isDownloadModalOpen = true"
            />
            <DownloadPaletteModal
              v-model="isDownloadModalOpen"
              :is-downloading="isDownloading"
              :primary-formats="primaryFormats"
              :adobe-formats="adobeFormats"
              :sketch-format="sketchFormat"
              :other-formats="otherFormats"
              @select="selectDownload"
            />
          </SectionColumn>
        </div>

        <PaletteTabs
          :tabs="tabs"
          :visual-count="totalVisualPaletteColors"
          :code-count="totalCodePaletteColors"
          :is-loading="isLoading"
          :dim="dimContent"
          @change="changePaletteView"
        />

        <SectionColumn v-if="totalVisualPaletteColors === 0 && tabs[0].current === true && isLoading == false" :dim="dimContent" inner-class="mt-8">
          <EmptyState />
        </SectionColumn>

        <SectionColumn v-if="isLoading" :dim="dimContent" inner-class="mt-8 mb-32">
          <div role="status" class="text-center">
            <Vue3Lottie v-if="Vue3Lottie" :animation-data="animationData" :width="200" />
            <p class="text-lg font-bold text-white">Generating...</p>
            <p class="text-sm text-gray-400">Please be patient. Our robots are working hard to extract every pixel.</p>
          </div>
        </SectionColumn>

        <SectionColumn v-if="totalCodePaletteColors === 0 && tabs[1].current === true && isLoading == false" :dim="dimContent" inner-class="mt-8">
          <EmptyState />
        </SectionColumn>

        <!-- Visual palette groups -->
        <ColorGroup
          v-if="response.visualPalette['Custom Colors'].length !== 0 && tabs[0].current == true && isLoading == false"
          title="Custom Colors"
          :colors="response.visualPalette['Custom Colors']"
          category="Custom Colors"
          :editable="enableEdit"
          :dim="dimContent"
          :is-selected="isColorSelected"
          @copy="copyToClipboard"
          @toggle-select="toggleSelection"
          @delete="(cat, obj, i) => deleteColorVisual(i, cat)"
        />
        <ColorGroup
          v-if="response.visualPalette['Brand Colors'].length !== 0 && tabs[0].current == true && isLoading == false"
          title="Brand Colors"
          :colors="response.visualPalette['Brand Colors']"
          category="Brand Colors"
          :editable="enableEdit"
          :dim="dimContent"
          :is-selected="isColorSelected"
          @copy="copyToClipboard"
          @toggle-select="toggleSelection"
          @delete="(cat, obj, i) => deleteColorVisual(i, cat)"
        />
        <ColorGroup
          v-if="response.visualPalette['Logo Colors'].length !== 0 && tabs[0].current == true && isLoading == false"
          title="Logo Colors"
          :colors="response.visualPalette['Logo Colors']"
          category="Logo Colors"
          :editable="enableEdit"
          :dim="dimContent"
          :is-selected="isColorSelected"
          @copy="copyToClipboard"
          @toggle-select="toggleSelection"
          @delete="(cat, obj, i) => deleteColorVisual(i, cat)"
        />
        <ColorGroup
          v-if="response.visualPalette['Homepage Colors'].length !== 0 && tabs[0].current == true && isLoading == false"
          title="Homepage Colors"
          :colors="response.visualPalette['Homepage Colors']"
          category="Homepage Colors"
          :editable="enableEdit"
          :dim="dimContent"
          :is-selected="isColorSelected"
          @copy="copyToClipboard"
          @toggle-select="toggleSelection"
          @delete="(cat, obj, i) => deleteColorVisual(i, cat)"
        />

        <!-- Nearest (code) palette groups.
             Revealed a few groups per frame (see useProgressiveList): the full
             code palette is regularly 150+ swatch cards, and mounting all of
             them inside the click that selects this tab was the slowest
             interaction on the page. -->
        <template v-if="codeGroups.length !== 0 && tabs[1].current === true && isLoading == false">
          <ColorGroup
            v-for="group in visibleCodeGroups"
            :key="group.name"
            :title="group.name"
            :colors="group.colors"
            :category="group.name"
            :editable="enableEdit"
            :dim="dimContent"
            :is-selected="isColorSelected"
            @copy="copyToClipboard"
            @toggle-select="toggleSelection"
            @delete="(grp, obj, i) => deleteColorNearest(grp, i)"
          />
        </template>

        <ApiCtaCard />

        <FeedbackBar :about-open="isAboutModalOpen" @about="openAbout" />

        <!-- "Are you happy with this palette?" — teleports itself to the bottom
             of the viewport, so its position in the tree doesn't matter; it
             lives here to sit beside the footer's Feedback button, which is the
             manual version of the same question. -->
        <ResultFeedbackBar :target="feedbackTarget" />
      </div>

      <!-- SEO/LLM content. Rendered unconditionally so the content is in the server HTML. -->
      <AboutProjectModal v-model="isAboutModalOpen" />
    </div>
  </NuxtLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import * as Sentry from '@sentry/nuxt'
import { trackEvent } from '~/composables/analytics'
import { response } from '~/assets/js/colorData.js'

import { usePaletteView } from '~/composables/usePaletteView'
import { usePaletteSelection } from '~/composables/usePaletteSelection'
import { usePaletteGeneration } from '~/composables/usePaletteGeneration'
import { usePaletteExport } from '~/composables/usePaletteExport'
import { useProgressiveList } from '~/composables/useProgressiveList'

import BaseHeading from '~/components/atoms/BaseHeading.vue'
import GradientText from '~/components/atoms/GradientText.vue'
import HeroBackdrop from '~/components/atoms/HeroBackdrop.vue'
import SiteHeaderRibbon from '~/components/organisms/SiteHeaderRibbon.vue'
import UrlGeneratorForm from '~/components/organisms/UrlGeneratorForm.vue'
import PaletteToolbar from '~/components/organisms/PaletteToolbar.vue'
import DownloadPaletteModal from '~/components/organisms/DownloadPaletteModal.vue'
import PaletteTabs from '~/components/organisms/PaletteTabs.vue'
import ApiCtaCard from '~/components/organisms/ApiCtaCard.vue'
import FeedbackBar from '~/components/organisms/FeedbackBar.vue'
import AboutProjectModal from '~/components/organisms/AboutProjectModal.vue'
import ResultFeedbackBar from '~/components/organisms/ResultFeedbackBar.vue'
import SectionColumn from '~/components/atoms/SectionColumn.vue'
import CollapseTransition from '~/components/atoms/CollapseTransition.vue'
import ColorGroup from '~/components/molecules/ColorGroup.vue'
import EmptyState from '~/components/molecules/EmptyState.vue'
import FilterToast from '~/components/molecules/FilterToast.vue'
import Notification from '~/components/molecules/Notification.vue'
import PrefetchCredit from '~/components/molecules/PrefetchCredit.vue'
import ToastStack from '~/components/molecules/ToastStack.vue'

import logo from '~/assets/img/colorize_design_logo.svg'
import { SITE_URL } from '~/utils/site'

useHead({
  title: 'Extract Color Palettes from Any Website - Free Color Palette Generator',
  meta: [
    { name: 'description', content: "Generate color palettes instantly from any website effortlessly. Enter a URL to explore color schemes and combinations directly from the site's design. No sign-up required. Try it now!" },
  ],
  link: [
    // Every generated/shared palette is a `?url=…` variant of this same
    // prerendered document (the brand pages link to them too). Point them all
    // at the bare homepage so search engines index one URL, not thousands.
    { rel: 'canonical', href: SITE_URL + '/' },
  ],
  htmlAttrs: {
    lang: 'en',
  },
})

useSeoMeta({
  description: "Generate color palettes instantly from any website effortlessly. Enter a URL to explore color schemes and combinations directly from the site's design. No sign-up required. Try it now!",
  ogTitle: 'Extract Color Palettes from Any Website - Free Color Palette Generator',
  ogDescription: 'Generate color palettes instantly from any website effortlessly. No sign-up required. Try it now!',
  // public/og-image.jpg — the .png this used to point at doesn't exist, so
  // shares rendered without a preview image.
  ogImage: `${SITE_URL}/og-image.jpg`,
  ogType: 'website',
  ogUrl: 'https://colorize.design',
})

const route = useRoute()

// --- Page-level UI state shared between organisms/composables ---------------
const screenshotLoaded = ref(false)
const screenshotAnimation = ref(false)
const scrollElement = ref(null)
const title = ref('Example Palette')
const isMobile = ref(false)

const isAboutModalOpen = ref(false)
const showNotification = ref(false)
const showNotificationColor = ref(false)
const showNotificationColorAdded = ref(false)

// --- Lazy Lottie -----------------------------------------------------------
// vue3-lottie (~300 KB) + animation.json (~46 KB) load on the first generation
// instead of eagerly at import time. The promise is memoised.
const Vue3Lottie = ref(null)
const animationData = ref(null)
let lottiePromise = null
const ensureLottie = () => {
  if (!lottiePromise) {
    lottiePromise = Promise.all([
      import('vue3-lottie'),
      import('../assets/img/animation.json'),
    ]).then(([mod, anim]) => {
      animationData.value = anim.default
      Vue3Lottie.value = mod.Vue3Lottie
    })
  }
  return lottiePromise
}

// --- Composables (the composition root wires them together) -----------------
const { tabs, changePaletteView, selectVisualTab, totalVisualPaletteColors, totalCodePaletteColors } = usePaletteView()

const {
  enableEdit,
  showFilter,
  editToggle,
  toggleSelection,
  isColorSelected,
  mergePalettes,
  resetFiltering,
  copyToClipboard,
  deleteColorVisual,
  deleteColorNearest,
  totalVisualSelectedColors,
  totalCodeSelectedColors,
} = usePaletteSelection({
  onColorCopied: () => {
    showNotificationColor.value = true
  },
})

const {
  urlData,
  typedUrl,
  hasError,
  hasWarning,
  hasSuccess,
  errorMsg,
  isLoading,
  settingToggle,
  mailingLists,
  token,
  turnstileOptions,
  setTurnstileRef,
  pendingGenerate,
  getUrl,
  toggleSettings,
  toggleCheck,
  setError,
  feedbackTarget,
} = usePaletteGeneration({ screenshotLoaded, screenshotAnimation, scrollElement, title, ensureLottie, resetFiltering })

const {
  isDownloadModalOpen,
  isDownloading,
  primaryFormats,
  adobeFormats,
  sketchFormat,
  otherFormats,
  selectDownload,
  // `typedUrl` is the site the current palette was generated from — it names the
  // downloaded file (colorize.design-stripe.com-palette.css).
} = usePaletteExport({ onError: setError, source: () => typedUrl.value })

// Fade the results column when there's no active URL yet.
const dimContent = computed(() => typedUrl.value === '' && isLoading.value === false)

// --- Code palette groups ----------------------------------------------------
// The nearest-color palette is a `{ group: colors[] }` object; flattened here so
// it can be sliced. Empty groups are dropped rather than rendered as nothing,
// which also keeps them out of the reveal budget below.
const codeGroups = computed(() =>
  Object.entries(response.nearestColors)
    .filter(([, colors]) => colors.length !== 0)
    .map(([name, colors]) => ({ name, colors })),
)

// Only reveal progressively while this tab is the one on screen; leaving it
// collapses the list again so coming back is just as cheap.
const codeTabActive = computed(() => tabs.value[1].current === true && isLoading.value === false)
const { visible: visibleCodeGroups } = useProgressiveList(() => codeGroups.value, {
  active: codeTabActive,
  initial: 4,
  step: 6,
})

// --- Handlers the page owns -------------------------------------------------
// A color sampled from the screenshot becomes a Custom Color and switches to
// the Visual tab.
const onScreenshotSampled = (color) => {
  showNotificationColorAdded.value = true
  try {
    trackEvent('Custom Color Add')
  } catch {}
  if (scrollElement.value) {
    scrollElement.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  selectVisualTab()
  response.visualPalette['Custom Colors'].push({ color, name: 'custom' })
}

// The screenshot finished loading (fired for both fresh and cached src).
const onScreenshotLoaded = () => {
  screenshotLoaded.value = true
  screenshotAnimation.value = false
}

const openNotification = () => {
  try {
    const currentUrl = `${window.location.origin}${route.fullPath}`
    navigator.clipboard.writeText(currentUrl).then(() => {}).catch(() => {})
    trackEvent('Share Clicked')
  } catch (error) {}
  showNotification.value = true
}

const openAbout = () => {
  isAboutModalOpen.value = true
  try {
    trackEvent('About Project Opened', { page: route.path })
  } catch {}
}

onMounted(() => {
  isMobile.value = /Mobi|Android/i.test(navigator.userAgent)

  // The Sentry user-feedback widget isn't needed for first paint. Build and
  // attach it during idle time so its setup never competes with hydration/LCP.
  const initFeedback = () => {
    try {
      const feedback = Sentry.feedbackIntegration({
        colorScheme: 'dark',
        isEmailRequired: true,
        buttonLabel: 'Feedback',
        submitButtonLabel: 'Send Feedback',
        formTitle: 'Send Feedback',
        triggerLabel: 'Send Feedback',
        showBranding: false,
        autoInject: false,
        messagePlaceholder: 'Found bugs? Do you have suggestions?',
        themeDark: {
          background: '#030712', // gray-950, the page background
          outline: 'none',
          // The site's own stack: plain 'Inter' only ever resolved through an
          // rsms.me @font-face the Tailwind config no longer emits.
          fontFamily: "'Inter Variable', 'Inter Fallback', sans-serif",
        },
      })
      const btn = document.querySelector('#feedback-button')
      if (btn) feedback.attachTo(btn, {})
    } catch {
      // Non-critical widget — never let its setup break the page.
    }
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(initFeedback, { timeout: 5000 })
  } else {
    setTimeout(initFeedback, 3000)
  }
})
</script>
